import React, { useMemo, useState } from "react";
import {
  OFFICIAL_CANTONS,
  PRIMARY_CANTON_ORDER_CODES,
  OTHER_CANTON_CODES,
} from "../../lib/constants";
import useYearRange from "../../hooks/useYearRange";
import { IndustryFilters, PhaseFilters, GenderFilters, CantonFilters, Section } from "./components";
import type { Filters, FilterOptions } from "./types";
import styles from "./FilterPanel.module.css";

// `Filters` and `FilterOptions` types are imported from ./types

type FilterPanelProps = {
  filters: Filters;
  filterOptions: FilterOptions;
  activeTab: string;
  activeChart: string;
  updateFilter: (name: string, value: any) => void;
  toggleArrayFilter: (name: string, value: string) => void;
  resetFilters: () => void;
  isMobile?: boolean;
};

const FilterPanel = ({
  filters,
  filterOptions,
  activeTab,
  activeChart,
  updateFilter,
  toggleArrayFilter,
  resetFilters,
  isMobile = false,
}: FilterPanelProps) => {
  const companiesTab = activeTab === "companies";
  const dealsTab = !companiesTab;

  // Accordion state for mobile
  const [openSections, setOpenSections] = useState({
    cantons: true,
    industries: true,
    phases: true,
    ceoGender: true,
  });

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Use centralized canton groupings: primary preview list + grouped "Other"
  const PRIMARY_CANTON_ORDER = PRIMARY_CANTON_ORDER_CODES as readonly string[];
  const OTHER_CANTON_CODES_LOCAL = OTHER_CANTON_CODES as readonly string[];

  const allowedCantons = useMemo(() => {
    const map = OFFICIAL_CANTONS.reduce(
      (acc: Record<string, any>, c: any) => {
        acc[c.code] = c;
        return acc;
      },
      {} as Record<string, any>
    );
    return PRIMARY_CANTON_ORDER.map((code) => map[code]).filter(Boolean);
  }, [PRIMARY_CANTON_ORDER]);

  const otherCantons = useMemo(
    () => OFFICIAL_CANTONS.filter((c: any) => OTHER_CANTON_CODES_LOCAL.includes(c.code)),
    [OTHER_CANTON_CODES_LOCAL]
  );

  // Determine which filter should be primary (checkboxes with colors)
  const isOverviewTab = activeChart === "timeline";
  const isCantonPrimary = activeChart === "canton";
  const isIndustryPrimary = activeChart === "industry";
  const isPhasePrimary = activeChart === "phase";
  const isGenderPrimary = activeChart === "ceoGender";

  // Year range options (hook must be called at top-level of component)
  const { startOptions, endOptions, startValue, endValue } = useYearRange(filterOptions, {
    yearRange: filters.yearRange,
  });

  return (
    <div className={styles.panelRoot}>
      {isOverviewTab ? (
        <div className={styles.filtersRowOverview}></div>
      ) : (
        <div className={styles.filtersRow}>
          {/* Years */}
          <Section plain>
            <div className={styles.inputGroup}>
              <select
                value={startValue}
                onChange={(e) =>
                  updateFilter("yearRange", [
                    Math.min(parseInt(e.target.value || "0", 10), filters.yearRange[1] - 1),
                    filters.yearRange[1],
                  ])
                }
                className={styles.inputSmall}
                disabled={startOptions.length === 0}
              >
                {startOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <span className={styles.textMuted}>to</span>
              <select
                value={endValue}
                onChange={(e) =>
                  updateFilter("yearRange", [
                    filters.yearRange[0],
                    Math.max(parseInt(e.target.value || "0", 10), filters.yearRange[0] + 1),
                  ])
                }
                className={styles.inputSmall}
                disabled={endOptions.length === 0}
              >
                {endOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </Section>

          {/* Industries */}
          <Section
            title="Industries"
            onReset={() => updateFilter("industries", [])}
            isAccordion={isMobile}
            isOpen={openSections.industries}
            onToggle={() => toggleSection("industries")}
            isActive={isIndustryPrimary}
          >
            <IndustryFilters
              filters={filters}
              filterOptions={filterOptions}
              updateFilter={updateFilter}
              toggleArrayFilter={toggleArrayFilter}
              isMobile={isMobile}
              isActive={isIndustryPrimary}
            />
          </Section>

          {/* Stages + CEO gender */}
          <div className={styles.stackColumn}>
            {dealsTab ? (
              <Section
                title="Stages"
                onReset={() => updateFilter("phases", [])}
                isAccordion={isMobile}
                isOpen={openSections.phases}
                onToggle={() => toggleSection("phases")}
                isActive={isPhasePrimary}
              >
                <PhaseFilters
                  filters={filters}
                  filterOptions={filterOptions}
                  updateFilter={updateFilter}
                  toggleArrayFilter={toggleArrayFilter}
                  isMobile={isMobile}
                  isActive={isPhasePrimary}
                />
              </Section>
            ) : (
              <Section title="Stages" onReset={() => updateFilter("phases", [])}>
                <div className="space-y-1.5">
                  <label className={`${styles.itemLabel}`}>
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      className={`${styles.checkbox}`}
                    />
                    <span className={styles.labelTextMuted}>All</span>
                  </label>
                </div>
              </Section>
            )}

            {/* CEO gender */}
            <Section
              title="CEO gender"
              onReset={() => updateFilter("ceoGenders", [])}
              isAccordion={isMobile}
              isOpen={openSections.ceoGender}
              onToggle={() => toggleSection("ceoGender")}
              isActive={isGenderPrimary}
            >
              <GenderFilters
                filters={filters}
                filterOptions={filterOptions}
                updateFilter={updateFilter}
                toggleArrayFilter={toggleArrayFilter}
                isMobile={isMobile}
                isActive={isGenderPrimary}
              />
            </Section>
          </div>

          {/* Cantons */}
          <Section
            title="Cantons"
            onReset={() => updateFilter("cantons", [])}
            isAccordion={isMobile}
            isOpen={openSections.cantons}
            onToggle={() => toggleSection("cantons")}
            isActive={isCantonPrimary}
          >
            <CantonFilters
              filters={filters}
              allowedCantons={allowedCantons}
              otherCantons={otherCantons}
              updateFilter={updateFilter}
              toggleArrayFilter={toggleArrayFilter}
              isMobile={isMobile}
              isActive={isCantonPrimary}
            />
          </Section>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
